/* eslint-disable class-methods-use-this */
import TopupBalance from '@beep/topup-standalone';
import classNames from 'classnames/bind';
import { NOT_DIGIT } from 'constants/common';
import PropTypes from 'prop-types';
import { pathOr } from 'ramda';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
  Button,
  DataBlocks,
  ErrorBlock,
  I18n,
  Link,
  Status,
  TextInput,
  Tooltip,
} from 'components';
import declint from 'utils/declint';
import {
  formatCurrency,
  formatDate,
  formatPhone,
  formatUnitString,
  getPaySumString,
} from 'utils/formatString';

import { browserReliableDetection } from 'utils/browserReliableDetection';
import { getMessage } from '../../../../components/I18n';
import { fetchData } from '../../actions';
import { balanceAnalytics } from '../../analytics';
import ApplePayButton from '../../components/ApplePayButton';
import PromisedPayment from '../../components/PromisedPayment';
import SpfButton from '../../components/SfpPayment/SpfButton';
import { SPF_TEXT_CONFIG } from '../../components/SfpPayment/sfpPaymentHelper';

import styles from './styles.pcss';

const cx = classNames.bind(styles);

class Balance extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isSafari: browserReliableDetection() === 'Safari',
      currentTimestamp: new Date(),
      paySum: props.shortfallAmount || 500,
      isTopUpWidgetOpen: false,
      paymentMethod: undefined,
    };
  }

  componentDidMount() {
    this.interval = setInterval(() => {
      this.setState({ currentTimestamp: new Date() });
    }, 1000);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  getLastBalanceUpdate() {
    const { balanceTimestamp } = this.props.data;
    const { currentTimestamp } = this.state;

    const millisecondsDiff = currentTimestamp - balanceTimestamp;
    const minutesDiff = Math.floor(millisecondsDiff / (1000 * 60));
    const hoursDiff = Math.floor(minutesDiff / 60);

    if (minutesDiff < 1) return 'Обновлено только что';

    const timeString = hoursDiff
      ? declint(hoursDiff, ['%s час', '%s часа', '%s часов'])
      : declint(minutesDiff, ['%s минуту', '%s минуты', '%s минут']);

    return `Обновлено ${timeString} назад`;
  }

  getPclDescription() {
    const {
      personalCreditLimit,
      balance,
    } = this.props.data;

    if (personalCreditLimit.isVersion2
      && !personalCreditLimit.isPermanentSoc) {
      return (
        <>
          <I18n
            className={cx('serviceDescription', 'temporary')}
            path="home.balance.personalCreditLimit.temporarySocDescription"
            vars={{ amount: personalCreditLimit.depositAmount }}
          />
          <Tooltip>
            <I18n path="home.balance.personalCreditLimit.depositTooltip" />
          </Tooltip>
        </>
      );
    }

    if (!personalCreditLimit.amount) {
      return null;
    }

    if (balance > 0) {
      return (
        <>
          <I18n
            className={cx('serviceDescription')}
            path="home.balance.personalCreditLimit.permanentSocDescription"
            vars={{ amount: -personalCreditLimit.amount }}
          />
          {personalCreditLimit.depositReturnDate
          && (
            <I18n
              className={cx('serviceDescription')}
              path="home.balance.personalCreditLimit.depositReturnDate"
              vars={{ date: formatDate(personalCreditLimit.depositReturnDate) }}
            />
          )}
        </>
      );
    }

    return (
      <>
        <I18n
          className={cx('serviceDescription')}
          path="home.balance.personalCreditLimit.permanentSocWithNegativeBalanceDescription"
          vars={{
            amount: -personalCreditLimit.amount,
            date: formatDate(personalCreditLimit.activeEndDate),
          }}
        />
        {personalCreditLimit.depositReturnDate
        && (
          <I18n
            className={cx('serviceDescription')}
            path="home.balance.personalCreditLimit.depositReturnDate"
            vars={{ date: formatDate(personalCreditLimit.depositReturnDate) }}
          />
        )}
      </>
    );
  }

  updateBalance = () => this.props.actions.fetchData(['Balance', 'Status'], false, 'balance');

  updatePaySum = (value) => {
    this.setState({
      paySum: Number(value.replace(NOT_DIGIT, '')),
    });
  };

  onPaymentClick = () => {
    const { data } = this.props;
    const { paySum } = this.state;

    this.pushClickAnalytics({
      [getMessage('home.balance.payButton')]: /mobile/i.test(navigator.userAgent)
        ? `https://oplata.beeline.ru/mob/?ctn=${data.ctn}&sum=${paySum}`
        : `${data.paymentUrl}?ctn=${data.ctn}&sum=${paySum}`,
    });

    this.setIsTopUpWidgetOpen(true);
  }

  setIsTopUpWidgetOpen = (isOpen, isSBP) => {
    this.setState({
      isTopUpWidgetOpen: isOpen,
      paymentMethod: isSBP ? 'sbp' : undefined,
    });
  }

  onBlurPaySum = () => {
    const { minPaymentAmount, maxPaymentAmount } = this.props.data;
    if (this.state.paySum < minPaymentAmount) {
      this.setState({ paySum: minPaymentAmount });
    }
    if (this.state.paySum > maxPaymentAmount) {
      this.setState({ paySum: maxPaymentAmount });
    }
    return this.state.paySum;
  };

  pushClickAnalytics(data) {
    balanceAnalytics.pushClickEvent(data);
  }

  renderError = (refreshType) => (
    <ErrorBlock
      className="halfWidth"
      title={<I18n path="home.balance.blockTitle" />}
      refreshAction={this.updateBalance}
      refreshType={refreshType}
    />
  );

  renderBalanceContent(data) {
    const { isSafari, paySum, isTopUpWidgetOpen, paymentMethod } = this.state;

    const isPaySumInValid = (+paySum < 100 || +paySum > 15000);

    return (
      <>
        <TopupBalance
          isOpen={isTopUpWidgetOpen}
          sum={paySum}
          accountNumber={data.ctn}
          prefilledAccountNumber={data.ctn}
          setIsOpen={(isOpen) => {
            this.setIsTopUpWidgetOpen(isOpen);
          }}
          paymentMethod={paymentMethod}
        />
        {data.balance !== undefined && data.currency !== undefined ? (
          <div className={cx('info')}>
            <div
              className={cx('infoValue', {
                minus: data.balance < 0,
              })}
            >
              {formatUnitString(data.balance, data.currency)}
            </div>
            <div className={cx('infoTime')}>
              {this.getLastBalanceUpdate()}
            </div>
            {this.props.isPorted && data.portedText && (
              <div className={cx('portedText')} dangerouslySetInnerHTML={{ __html: data.portedText }} />
            )}
          </div>
        ) : (
          <I18n path="home.balance.unavailable" />
        )}

        {data.happyTime && (
          <div className={cx('message')}>
            {data.happyTime.isAccepted ? (
              <I18n
                path="home.balance.happyTime.activated"
                vars={{
                  percent: data.happyTime.bonusPercent,
                  date: data.happyTime.date,
                }}
              />
            ) : (
              <I18n
                path="home.balance.happyTime.available"
                vars={{
                  percent: data.happyTime.bonusPercent,
                }}
              />
            )}
          </div>
        )}

        {data.personalCreditLimit && data.personalCreditLimitLink && (
          <div className={cx('personalCreditLimitContainer')}>
            {data.personalCreditLimit.isVersion2 && !data.personalCreditLimit.isPermanentSoc ? (
              <>
                <i className={cx('statusIndicator', 'warn')} />
                <Link
                  className={['hideVisitedColor', cx('personalCreditLimitTitle')]}
                  href={data.personalCreditLimitLink.url}
                >
                  {data.personalCreditLimitLink.title}
                </Link>
              </>
            ) : (
              <>
                <i className={cx('statusIndicator')} />
                <Link
                  className={['hideVisitedColor', cx('personalCreditLimitTitle', 'permanent')]}
                  href={data.personalCreditLimitLink.url}
                >
                  {data.personalCreditLimitLink.title}
                </Link>
              </>
            )}
            <br />
            {this.getPclDescription()}
            {' '}
            {getPaySumString(data.minPaymentAmount, data.maxPaymentAmount)}
          </div>
        )}
        {data.messages && data.messages.map((message) => (
          <div className={cx('message')} key={message.text}>
            {message.isError ? (
              <Status className={['error', 'compact', cx('error')]}>
                <span dangerouslySetInnerHTML={{ __html: formatCurrency(message.text) }} />
                {message.tooltip && (
                  <Tooltip>
                    {message.tooltip}
                  </Tooltip>
                )}
              </Status>
            ) : (
              <>
                <span dangerouslySetInnerHTML={{ __html: formatCurrency(message.text) }} />
                {message.tooltip && (
                  <Tooltip>
                    {message.tooltip}
                  </Tooltip>
                )}
              </>
            )}
          </div>
        ))}

        {data.autoPaymentParams && data.autoPaymentParams.autoMtopupPayment && (
          <div className={cx('paymentService')}>
            <i className={cx('statusIndicator')} />
            <Link href={data.autoPaymentParams.autoMtopupPayment.url}>
              <I18n className={cx('paymentServiceTitle')} path="home.balance.autoMtopupPayment" />
            </Link>
          </div>
        )}

        {data.autoPaymentParams && data.autoPaymentParams.autoBankPayment && (
          <div className={cx('paymentService')}>
            <i className={cx('statusIndicator')} />
            <Link href={data.autoPaymentParams.autoBankPayment.url}>
              <I18n className={cx('paymentServiceTitle')} path="home.balance.autoBankPayment" />
            </Link>
          </div>
        )}

        {data.promisedPayment && data.promisedPaymentLink && (
          <PromisedPayment
            promisedPayment={data.promisedPayment}
            promisedPaymentLink={data.promisedPaymentLink}
            currency={data.currency}
          />
        )}

        { !this.props.hidePayForm && (
          <>
            <div className={cx('payForm')}>
              <div>
                <TextInput
                  className={cx('payInput')}
                  value={paySum}
                  onChange={this.updatePaySum}
                  onBlur={this.onBlurPaySum}
                />
                <span className={cx('payCurrency')}>
                  ₽
                </span>
                <div className={cx('paySumTooltip')}>
                  {getPaySumString(data.minPaymentAmount, data.maxPaymentAmount)}
                </div>
              </div>
              <div className={cx('payButton')}>
                <Button
                  wide
                  onClick={this.onPaymentClick}
                >
                  <I18n path="home.balance.payButton" />
                </Button>
              </div>
            </div>

            {data.isApplePayEnabled && isSafari && (
              <ApplePayButton
                className={cx('applePayButton')}
                paymentRequest={{
                  countryCode: 'RU',
                  currencyCode: 'RUB',
                  total: {
                    label: formatPhone(data.ctn),
                    amount: +paySum,
                  },
                  supportedNetworks: ['masterCard', 'visa'],
                  merchantCapabilities: ['supports3DS'],
                  requiredShippingContactFields: ['email'],
                }}
                paymentData={{ sum: +paySum, number: data.ctn }}
                disabled={isPaySumInValid}
              />
            )}
            <SpfButton
              onClick={
                () => {
                  this.pushClickAnalytics(SPF_TEXT_CONFIG.spfButtonText);
                  this.setIsTopUpWidgetOpen(true, true);
                }
              }
              disabled={isPaySumInValid}
            />
          </>
        ) }
      </>
    );
  }

  render() {
    const { data, error, isPorted } = this.props;

    if (!data) return null;

    const refreshType = 'balance';
    const isBlockedByBalance = !isPorted && data.status !== 'Active' && !data.canChangeStatus;
    const blockLinks = [];

    if (!data.balanceTimestamp) return null;

    if (!data.promisedPayment && !data.personalCreditLimit && data.promisedPaymentLink) {
      blockLinks.push({
        children: data.promisedPaymentLink.title,
        href: data.promisedPaymentLink.url,
        onClick: () => {
          this.pushClickAnalytics({
            [data.promisedPaymentLink.title]: data.promisedPaymentLink.url,
          });
        },
      });
    }
    if (!data.promisedPayment && !data.personalCreditLimit && data.personalCreditLimitLink) {
      blockLinks.push({
        children: data.personalCreditLimitLink.title,
        href: data.personalCreditLimitLink.url,
        onClick: () => {
          this.pushClickAnalytics({
            [data.personalCreditLimitLink.title]: data.personalCreditLimitLink.url,
          });
        },
      });
    }

    return (
      <DataBlocks.Block
        className={['halfWidth', { alert: isBlockedByBalance }]}
        title={<I18n path={`home.balance.blockTitle.${data.paymentType.toLowerCase()}`} />}
        refreshAction={this.updateBalance}
        links={!isPorted && blockLinks}
      >
        { this.props.isUserBalanceFetching || (error && error.type || data.error && data.error.type)
          ? this.renderError(refreshType)
          : this.renderBalanceContent(data)}
      </DataBlocks.Block>
    );
  }
}

function mapStateToProps(state) {
  let balanceObject = { data: null };

  if (!state.home.balance.error) {
    balanceObject = {
      data: {
        balanceTimestamp: state.home.balanceTimestamp,
        balanceNotification: state.home.balanceNotification.data,
        ...state.home.balance.data,
        ...state.home.status.data,
        ...state.home.profileSummary.data,
      },
      error: state.home.balance.error,
      isUserBalanceFetching: state.home.isUserBalanceFetching,
      isPorted: state.application.isPorted,
      hidePayForm: state.application.isPorted
        || state.application.isPostpaid && pathOr([], ['data', 'invoices'], state.home.balance).length > 0,
    };
  }

  return {
    ...balanceObject,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ fetchData }, dispatch),
  };
}

Balance.propTypes = {
  data: PropTypes.object,
  error: PropTypes.object,
  actions: PropTypes.object,
  shortfallAmount: PropTypes.number,
  isUserBalanceFetching: PropTypes.bool,
  hidePayForm: PropTypes.bool,
  isPorted: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(Balance);
